import axios, { type AxiosRequestConfig } from "axios";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

function stringifyUnknown(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function extractApiErrorMessage(data: unknown): string {
  if (data == null) return "서버 에러가 발생했습니다.";
  if (typeof data === "string") return data;
  if (typeof data !== "object") return stringifyUnknown(data);

  const obj = data as {
    detail?: unknown;
    message?: unknown;
    errors?: unknown;
  };

  const details = obj.detail ?? obj.message ?? obj.errors;
  if (Array.isArray(details)) {
    const joined = details
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const rec = d as {
            detail?: unknown;
            msg?: unknown;
            message?: unknown;
          };
          return stringifyUnknown(rec.detail ?? rec.msg ?? rec.message ?? d);
        }
        return stringifyUnknown(d);
      })
      .filter(Boolean)
      .join("\n");
    return joined || "서버 에러가 발생했습니다.";
  }

  if (details !== undefined) {
    return stringifyUnknown(details) || "서버 에러가 발생했습니다.";
  }

  return "서버 에러가 발생했습니다.";
}

axiosInstance.interceptors.response.use(
  (response) => {
    // HTML 응답 체크 (서버 에러 페이지 등) - 404처럼 처리, toast 표시 안 함
    const contentType = response.headers["content-type"] || "";
    if (contentType.includes("text/html")) {
      console.warn(
        "[Axios] Received HTML response instead of JSON, treating as 404"
      );
      const error = new Error("Server returned HTML instead of JSON");
      const axiosError = error as {
        response?: { status?: number };
        isAxiosError?: boolean;
        skipToast?: boolean;
      };
      axiosError.response = { status: 404 }; // 404로 처리하여 폴링에서 null 반환
      axiosError.isAxiosError = true;
      axiosError.skipToast = true; // toast 표시 안 함
      return Promise.reject(axiosError);
    }
    return response.data;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      // HTML 응답 체크 (에러 케이스) - toast 표시 안 함
      const contentType = error.response?.headers?.["content-type"] || "";
      if (contentType.includes("text/html")) {
        console.warn(
          "[Axios] Received HTML response in error case, treating as 404"
        );
        const htmlError = error as {
          response?: { status?: number };
          isAxiosError?: boolean;
          message?: string;
          skipToast?: boolean;
        };
        htmlError.response = { status: 404 };
        htmlError.isAxiosError = true;
        htmlError.message = "서버가 HTML을 반환했습니다.";
        htmlError.skipToast = true; // toast 표시 안 함
        return Promise.reject(htmlError);
      }

      const message = extractApiErrorMessage(error.response?.data);

      // 에러 메시지를 error 객체에 추가
      error.message = stringifyUnknown(message) || "서버 에러가 발생했습니다.";
    } else {
      error.message = "알 수 없는 에러가 발생했습니다.";
    }

    return Promise.reject(error);
  }
);

const httpClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.get<T, T>(url, config),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.post<T, T>(url, data, config),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.put<T, T>(url, data, config),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.patch<T, T>(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.delete<T, T>(url, config),
};

export default httpClient;
